import { useEffect, useRef, useState } from "react";

export default function BookDetailEdit({
  active,
  fromNextUp,
  T,
  SANS,
  DISPLAY_SERIF,
  STATUS,
  FMTS,
  BookCover,
  Chip,
  Sec,
  GenrePicker,
  MonthYearPicker,
  HalfStars,
  BigBtn,
  inp,
  dStatus,
  setDStatus,
  dTitle,
  setDTitle,
  dFmts,
  setDFmts,
  dSeriesName,
  setDSeriesName,
  dSeriesNum,
  setDSeriesNum,
  dEdition,
  setDEdition,
  dCover,
  setDCover,
  dIsbn,
  setDIsbn,
  dPublishedDate,
  setDPublishedDate,
  dPublisher,
  setDPublisher,
  dPages,
  setDPages,
  dGenres,
  setDGenres,
  dGenreLane,
  setDGenreLane,
  normalizeGenreLane,
  restrictGenresToLane,
  dVibes,
  setDVibes,
  dTags,
  setDTags,
  detailGenreOpen,
  setDetailGenreOpen,
  dStartDate,
  setDStartDate,
  dEndDate,
  setDEndDate,
  dRating,
  setDRating,
  dReads,
  setDReads,
  dRereading,
  dRereadStartDate,
  dNotes,
  setDNotes,
  dDesc,
  setDDesc,
  dFav,
  setDFav,
  editSection,
  detailEditScrollRef,
  onBack,
  onDelete,
  onLogReread,
  onFindCoverCandidates,
  onSave,
}) {
  const bookInfoRef = useRef(null);
  const [coverToolsOpen, setCoverToolsOpen] = useState(false);
  const [coverCandidates, setCoverCandidates] = useState([]);
  const [coverLookupBusy, setCoverLookupBusy] = useState(false);
  const [coverLookupError, setCoverLookupError] = useState("");
  const thinInp = {
    ...inp,
    padding: "9px 10px",
    borderRadius: 9,
    fontSize: 13,
    lineHeight: 1.35,
  };
  const formatRereadDate = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  const normalizedReads = (dReads || []).map((entry, index) => {
    const started = entry?.started || entry?.startDate || "";
    const finished = entry?.finished || entry?.endDate || entry?.date || "";
    const days =
      entry?.days !== undefined && entry?.days !== null
        ? Number(entry.days)
        : started && finished
          ? Math.max(
              1,
              Math.round(
                (new Date(finished) - new Date(started)) / (1000 * 60 * 60 * 24),
              ),
            )
          : null;
    return {
      key: `${started || finished || entry?.date || "reread"}-${index}`,
      label: `Reread #${index + 1}`,
      started,
      finished,
      days,
    };
  });

  useEffect(() => {
    if (editSection === "bookinfo") {
      bookInfoRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }, [editSection]);

  useEffect(() => {
    setCoverToolsOpen(false);
    setCoverCandidates([]);
    setCoverLookupError("");
    setCoverLookupBusy(false);
  }, [active.uid]);

  async function findCovers() {
    if (coverLookupBusy || !onFindCoverCandidates) return;
    setCoverLookupBusy(true);
    setCoverLookupError("");
    try {
      const found = await onFindCoverCandidates(active, dIsbn);
      setCoverCandidates(Array.isArray(found) ? found : []);
      if (!found?.length) {
        setCoverLookupError(
          "No provider cover was found yet. You can still paste an image URL below.",
        );
      }
    } catch {
      setCoverCandidates([]);
      setCoverLookupError(
        "Cover lookup is unavailable right now. You can still paste an image URL below.",
      );
    } finally {
      setCoverLookupBusy(false);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
      }}
    >
      <div style={{ position: "relative", minHeight: 160, flexShrink: 0 }}>
        <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          {dCover && (
            <img
              src={dCover}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "blur(22px) brightness(0.45) saturate(1.3)",
                transform: "scale(1.15)",
              }}
            />
          )}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to bottom,rgba(0,0,0,0.5) 0%,rgba(0,0,0,0.3) 45%," +
                T.bg +
                ")",
              boxShadow: "inset 0 -60px 90px rgba(0,0,0,0.45)",
            }}
          />
        </div>
        <div
          style={{
            position: "relative",
            zIndex: 2,
            padding: "20px 20px 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(8px)",
              border: "none",
              borderRadius: 9,
              padding: "7px 14px",
              color: "#fff",
              cursor: "pointer",
              fontSize: 14,
              fontFamily: SANS,
            }}
          >
            ← Back
          </button>
          <div
            style={{
              fontFamily: DISPLAY_SERIF,
              fontWeight: 700,
              fontSize: 18,
              color: "#fff",
              textShadow: "0 2px 8px rgba(0,0,0,0.6)",
            }}
          >
            Edit Book
          </div>
          <button
            onClick={onDelete}
            style={{
              width: 38,
              height: 38,
              borderRadius: 9,
              background: "rgba(196,88,74,0.25)",
              border: "1px solid rgba(196,88,74,0.2)",
              cursor: "pointer",
              fontSize: 15,
            }}
          >
            🗑️
          </button>
        </div>
        <div
          style={{
            position: "relative",
            zIndex: 2,
            padding: "16px 20px 20px",
            display: "flex",
            gap: 12,
            alignItems: "center",
          }}
        >
          <BookCover book={{ ...active, cover: dCover || null }} w={46} h={64} r={8} />
          <div
            style={{
              minWidth: 0,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <div
              style={{
                fontFamily: DISPLAY_SERIF,
                fontWeight: 600,
                fontSize: 16,
                color: "#fff",
                lineHeight: 1.2,
                textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {dTitle || active.title}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.78)",
                fontStyle: "italic",
                marginTop: 2,
                textShadow: "0 1px 4px rgba(0,0,0,0.55)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {active.authors}
            </div>
          </div>
        </div>
      </div>

      <div
        ref={detailEditScrollRef}
        style={{ flex: 1, overflowY: "auto", padding: "20px 20px 48px" }}
      >
        {!fromNextUp && (
          <Sec label="Status">
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              {Object.keys(STATUS).map((s) => (
                <Chip key={s} on={dStatus === s} onClick={() => setDStatus(s)}>
                  {STATUS[s].label}
                </Chip>
              ))}
            </div>
          </Sec>
        )}
        <Sec label="Book Title">
          <input
            value={dTitle}
            onChange={(e) => setDTitle(e.target.value)}
            placeholder="Book title"
            style={{ ...thinInp }}
          />
        </Sec>
        <Sec label="Format">
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {Object.entries(FMTS).map(([f, icon]) => (
              <Chip
                key={f}
                on={(dFmts || []).includes(f)}
                onClick={() =>
                  setDFmts((p) =>
                    (p || []).includes(f)
                      ? (p || []).filter((x) => x !== f)
                      : [...(p || []), f],
                  )
                }
              >
                {icon} {f}
              </Chip>
            ))}
          </div>
        </Sec>
        <Sec label="Series">
          <div style={{ display: "flex", gap: 10 }}>
            <input
              key={active.uid + "sname"}
              defaultValue={dSeriesName}
              onBlur={(e) => setDSeriesName(e.target.value)}
              placeholder="Series name..."
              style={{ ...thinInp, flex: 2 }}
            />
            <input
              key={active.uid + "snum"}
              defaultValue={dSeriesNum}
              onBlur={(e) => setDSeriesNum(e.target.value)}
              placeholder="#"
              style={{ ...thinInp, flex: 1 }}
            />
          </div>
        </Sec>
        <Sec label="Edition">
          <input
            key={active.uid + "edition"}
            defaultValue={dEdition}
            onBlur={(e) => setDEdition(e.target.value)}
            placeholder="Special, deluxe, anniversary..."
            style={{ ...thinInp }}
          />
        </Sec>
        <Sec label="Cover">
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              padding: 12,
              borderRadius: 11,
              border: "1px solid " + T.border,
              background: T.s2,
            }}
          >
            <BookCover book={{ ...active, cover: dCover || null }} w={52} h={74} r={8} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>
                {dCover ? "Selected cover" : "No cover selected"}
              </div>
              <div style={{ fontSize: 12, color: T.mute, lineHeight: 1.45, marginTop: 3 }}>
                Search ISBNdb and Google Books, or paste a direct image URL.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCoverToolsOpen((open) => !open)}
              style={{
                padding: "8px 10px",
                borderRadius: 9,
                border: "1px solid " + T.border,
                background: T.surface,
                color: T.sub,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: SANS,
                flexShrink: 0,
              }}
            >
              {coverToolsOpen ? "Done" : "Change"}
            </button>
          </div>
          {coverToolsOpen && (
            <div
              style={{
                marginTop: 10,
                padding: 12,
                borderRadius: 11,
                border: "1px solid " + T.border,
                background: T.surface,
              }}
            >
              <button
                type="button"
                onClick={findCovers}
                disabled={coverLookupBusy}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 9,
                  border: "none",
                  background: coverLookupBusy ? T.border : T.accent,
                  color: coverLookupBusy ? T.mute : "#fff",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: coverLookupBusy ? "default" : "pointer",
                  fontFamily: SANS,
                }}
              >
                {coverLookupBusy ? "Looking for covers…" : "Find Cover Options"}
              </button>
              {coverLookupError && (
                <div style={{ fontSize: 12, color: T.mute, lineHeight: 1.5, marginTop: 9 }}>
                  {coverLookupError}
                </div>
              )}
              {coverCandidates.length > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
                    gap: 9,
                    marginTop: 11,
                  }}
                >
                  {coverCandidates.map((candidate, index) => {
                    const selected = candidate.url === dCover;
                    return (
                      <button
                        key={`${candidate.url}-${index}`}
                        type="button"
                        onClick={() => setDCover(candidate.url)}
                        aria-label={`Use ${candidate.source} cover${candidate.detail ? `, ${candidate.detail}` : ""}`}
                        style={{
                          padding: 5,
                          borderRadius: 10,
                          border: "2px solid " + (selected ? T.accent : T.border),
                          background: selected ? T.accentBg : T.s2,
                          cursor: "pointer",
                          textAlign: "left",
                          fontFamily: SANS,
                        }}
                      >
                        <img
                          src={candidate.url}
                          alt=""
                          style={{
                            width: "100%",
                            aspectRatio: "2 / 3",
                            objectFit: "cover",
                            borderRadius: 6,
                            display: "block",
                            background: T.border,
                          }}
                        />
                        <div
                          style={{
                            fontSize: 10,
                            color: selected ? T.accent : T.sub,
                            fontWeight: 700,
                            marginTop: 5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {candidate.source}
                        </div>
                        {candidate.detail && (
                          <div
                            style={{
                              fontSize: 9,
                              color: T.mute,
                              marginTop: 2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {candidate.detail}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <div style={{ fontSize: 11, color: T.mute, margin: "12px 0 6px" }}>
                Direct image URL
              </div>
              <input
                value={dCover || ""}
                onChange={(e) => setDCover(e.target.value)}
                placeholder="https://example.com/book-cover.jpg"
                inputMode="url"
                style={{ ...thinInp, marginBottom: 8 }}
              />
              {dCover && (
                <button
                  type="button"
                  onClick={() => setDCover("")}
                  style={{
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    color: "#C4584A",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: SANS,
                  }}
                >
                  Remove cover
                </button>
              )}
            </div>
          )}
        </Sec>
        <Sec label="Genre, Vibes & Fun Stuff">
          <GenrePicker
            selectedGenres={dGenres}
            onGenres={setDGenres}
            lane={dGenreLane}
            onLaneChange={(nextLane) => {
              const normalized = normalizeGenreLane(nextLane);
              setDGenreLane(normalized);
              setDGenres((prev) => restrictGenresToLane(prev, normalized));
            }}
            selectedVibes={dVibes}
            onVibes={setDVibes}
            customTags={dTags}
            onTags={setDTags}
            T={T}
            openState={detailGenreOpen}
            setOpenState={setDetailGenreOpen}
          />
        </Sec>
        {!fromNextUp && (
          <Sec label="Reading Dates">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <MonthYearPicker
                value={dStartDate}
                onChange={setDStartDate}
                label="Started"
                T={T}
              />
              <MonthYearPicker
                value={dEndDate}
                onChange={setDEndDate}
                label="Finished"
                T={T}
              />
            </div>
          </Sec>
        )}
        <Sec label="Rating">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <HalfStars val={dRating} onChange={setDRating} />
            <Chip on={dFav} onClick={() => setDFav((f) => !f)}>
              {dFav ? "❤️ Favorited" : "Add to Favorites"}
            </Chip>
          </div>
        </Sec>
        {dRereading && dRereadStartDate && (
          <Sec label="Reread in Progress">
            <div
              style={{
                padding: "10px 12px",
                background: T.s2,
                borderRadius: 9,
                border: "1px solid " + T.border,
              }}
            >
              <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 3 }}>
                Started reread {formatRereadDate(dRereadStartDate)}
              </div>
              <div style={{ fontSize: 12, color: T.mute }}>
                Finish it from the book details page when you're done.
              </div>
            </div>
          </Sec>
        )}
        {normalizedReads.length > 0 && (
          <Sec label="Re-reads">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {normalizedReads.map((r, i) => (
                <div
                  key={r.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "8px 12px",
                    background: T.s2,
                    borderRadius: 9,
                    border: "1px solid " + T.border,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13, color: T.sub }}>
                      {r.label}
                    </span>
                    {r.started ? (
                      <>
                        <span style={{ display: "block", fontSize: 12, color: T.text, marginTop: 2 }}>
                          {formatRereadDate(r.started)} → {formatRereadDate(r.finished)}
                        </span>
                        <span style={{ display: "block", fontSize: 12, color: T.mute, marginTop: 2 }}>
                          {r.days !== null && r.days !== undefined
                            ? `${r.days} day${r.days === 1 ? "" : "s"}`
                            : "Completed"}
                        </span>
                      </>
                    ) : (
                      <span style={{ display: "block", fontSize: 12, color: T.mute, marginTop: 2 }}>
                        {formatRereadDate(r.finished)}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setDReads((prev) => prev.filter((_, idx) => idx !== i))}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      border: "1px solid " + T.border,
                      background: "transparent",
                      color: T.sub,
                      cursor: "pointer",
                      fontSize: 18,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                    aria-label={`Remove re-read ${i + 1}`}
                    title="Remove re-read"
                  >
                    −
                  </button>
                </div>
              ))}
            </div>
          </Sec>
        )}
        {!fromNextUp && dStatus === "Read" && !dRereading && (
          <div style={{ marginBottom: 22 }}>
            <button
              onClick={onLogReread}
              style={{
                width: "100%",
                padding: "11px",
                borderRadius: 10,
                border: "1px solid " + T.border,
                background: T.s2,
                color: T.sub,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: SANS,
              }}
            >
              Start Reread
            </button>
          </div>
        )}
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: T.mute,
              marginBottom: 10,
            }}
          >
            Notes
          </div>
          <textarea
            value={dNotes}
            onChange={(e) => setDNotes(e.target.value)}
            placeholder="Your thoughts, favorite quotes..."
            rows={5}
            style={{ ...inp, resize: "vertical", lineHeight: 1.7 }}
          />
        </div>
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: T.mute,
              marginBottom: 10,
            }}
          >
            About
          </div>
          <textarea
            value={dDesc}
            onChange={(e) => setDDesc(e.target.value)}
            placeholder="Add or correct the book blurb..."
            rows={7}
            style={{ ...inp, resize: "vertical", lineHeight: 1.7 }}
          />
        </div>
        <div ref={bookInfoRef}>
          <Sec label="Book Info">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                key={active.uid + "-isbn"}
                value={dIsbn}
                onChange={(e) => setDIsbn(e.target.value)}
                placeholder="Add ISBN"
                style={{ ...thinInp }}
              />
              <input
                key={active.uid + "-published"}
                defaultValue={dPublishedDate}
                onBlur={(e) => setDPublishedDate(e.target.value)}
                placeholder="March 17, 2026"
                style={{ ...thinInp }}
              />
              <input
                key={active.uid + "-publisher"}
                defaultValue={dPublisher}
                onBlur={(e) => setDPublisher(e.target.value)}
                placeholder="Add publisher"
                style={{ ...thinInp }}
              />
              <input
                key={active.uid + "-pages"}
                defaultValue={dPages}
                onBlur={(e) => setDPages(e.target.value)}
                placeholder="Add page count"
                inputMode="numeric"
                style={{ ...thinInp }}
              />
            </div>
          </Sec>
        </div>
        <BigBtn onClick={onSave}>Save Changes</BigBtn>
      </div>
    </div>
  );
}
